import React, { useState, useRef } from 'react';
import { sendDirectMessage } from './utils/directMessage';
import './DirectMessageTest.scss';

/**
 * 채팅방 없이 직접 메시지를 전송하는 테스트 컴포넌트
 * kuku1234(관리자) -> hong1(학생)으로 메시지 전송
 */
const DirectMessageTest = () => {
    const [message, setMessage] = useState('');
    const [sending, setSending] = useState(false);
    const [result, setResult] = useState(null);
    const [selectedImage, setSelectedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const fileInputRef = useRef(null);
    const acaCode ='FD2001'; // 테스트용 학원 코드

    //=========================================================================

    // 발신자 정보 (관리자)
    const SENDER_ID = acaCode + '_kuku1234';
    const SENDER_NAME = '관리자';
    
    // 수신자 정보 (학생)
    const RECEIVER_ID = acaCode + '_hong1';
    const RECEIVER_NAME = '학생';

    const chatRoomName = `AI K-EDU 학원`;

    // 이미지 선택
    const handleImageSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            alert('이미지 파일만 선택할 수 있습니다.');
            return;
        }

        setSelectedImage(file);
        
        // 미리보기 생성
        const reader = new FileReader();
        reader.onloadend = () => {
            setImagePreview(reader.result);
        };
        reader.readAsDataURL(file);
    };

    // 이미지 선택 취소
    const handleImageCancel = () => {
        setSelectedImage(null);
        setImagePreview(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // 텍스트 메시지 전송
    const handleSendMessage = async () => {
        if (!message.trim()) {
            alert('메시지를 입력해주세요.');
            return;
        }

        setSending(true);
        setResult(null);

        try {
            const response = await sendDirectMessage(
                chatRoomName,
                SENDER_ID,
                SENDER_NAME,
                RECEIVER_ID,
                message
            );

            if (response.success) {
                setResult({
                    type: 'success',
                    message: '메시지가 성공적으로 전송되었습니다!',
                    messageId: response.messageId,
                    chatRoomId: response.chatRoomId
                });
                setMessage(''); // 입력창 초기화
            } else {
                setResult({
                    type: 'error',
                    message: '메시지 전송 실패: ' + response.error
                });
            }
        } catch (error) {
            setResult({
                type: 'error',
                message: '오류 발생: ' + error.message
            });
        } finally {
            setSending(false);
        }
    };

    // 이미지 메시지 전송
    const handleSendImageMessage = async () => {
        if (!selectedImage) {
            alert('이미지를 선택해주세요.');
            return;
        }

        setSending(true);
        setResult(null);

        try {
            // Firebase Storage에 이미지 업로드
            const { ref: storageRef, uploadBytesResumable, getDownloadURL } = await import('firebase/storage');
            const { storage } = await import('./firebaseConfig');
            
            const timestamp = Date.now();
            const randomId = Math.random().toString(36).substring(2, 9);
            const fileName = `images/${timestamp}_${randomId}_${selectedImage.name}`;
            const imageRef = storageRef(storage, fileName);
            
            console.log('이미지 업로드 시작:', fileName);
            
            const uploadTask = uploadBytesResumable(imageRef, selectedImage);
            
            const downloadURL = await new Promise((resolve, reject) => {
                uploadTask.on(
                    'state_changed',
                    (snapshot) => {
                        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                        console.log(`이미지 업로드 진행률: ${progress}%`);
                    },
                    (error) => {
                        console.error('이미지 업로드 실패:', error);
                        reject(error);
                    },
                    async () => {
                        const url = await getDownloadURL(uploadTask.snapshot.ref);
                        resolve(url);
                    }
                );
            });

            // 이미지 데이터 구성
            const imageData = [{
                url: downloadURL,
                name: selectedImage.name,
                size: selectedImage.size,
                type: 'image'
            }];

            // 이미지 메시지 전송
            const response = await sendDirectMessage(
                SENDER_ID,
                SENDER_NAME,
                RECEIVER_ID,
                message.trim() || '이미지',
                'image',
                imageData
            );

            if (response.success) {
                setResult({
                    type: 'success',
                    message: '이미지가 성공적으로 전송되었습니다!',
                    messageId: response.messageId,
                    chatRoomId: response.chatRoomId
                });
                setMessage('');
                handleImageCancel();
            } else {
                setResult({
                    type: 'error',
                    message: '이미지 전송 실패: ' + response.error
                });
            }
        } catch (error) {
            console.error('이미지 전송 오류:', error);
            setResult({
                type: 'error',
                message: '오류 발생: ' + error.message
            });
        } finally {
            setSending(false);
        }
    };

   

    //=========================================================================

    return (
        <div className="direct-message-test">
            <div className="test-container">
                <h2>📱 직접 메시지 전송 테스트</h2>
                
                <div className="user-info">
                    <div className="sender">
                        <strong>발신자:</strong> {SENDER_NAME} ({SENDER_ID})
                    </div>
                    <div className="arrow">→</div>
                    <div className="receiver">
                        <strong>수신자:</strong> {RECEIVER_NAME} ({RECEIVER_ID})
                    </div>
                </div>

                <div className="message-input-section">
                    <h3>텍스트 메시지</h3>
                    <textarea
                        className="message-textarea"
                        placeholder="전송할 메시지를 입력하세요..."
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        disabled={sending}
                        rows={4}
                    />
                    
                    <button 
                        className="send-button"
                        onClick={handleSendMessage}
                        disabled={sending || !message.trim()}
                    >
                        {sending ? '전송 중...' : '텍스트 메시지 전송'}
                    </button>
                </div>

                <div className="image-input-section">
                    <h3>이미지 메시지</h3>
                    
                    <div className="image-upload-area">
                        <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleImageSelect}
                            accept="image/*"
                            style={{ display: 'none' }}
                        />
                        
                        {!imagePreview ? (
                            <button 
                                className="select-image-button"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={sending}
                            >
                                📷 이미지 선택
                            </button>
                        ) : (
                            <div className="image-preview-container">
                                <div className="image-preview">
                                    <img src={imagePreview} alt="미리보기" />
                                    <button 
                                        className="remove-image-button"
                                        onClick={handleImageCancel}
                                        disabled={sending}
                                    >
                                        ✕
                                    </button>
                                </div>
                                <div className="image-info">
                                    <p>{selectedImage?.name}</p>
                                    <p>{(selectedImage?.size / 1024).toFixed(2)} KB</p>
                                </div>
                            </div>
                        )}
                    </div>

                    {imagePreview && (
                        <>
                            <input
                                type="text"
                                className="image-caption-input"
                                placeholder="이미지 설명 (선택사항)"
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                disabled={sending}
                            />
                            <button 
                                className="send-button send-image-button"
                                onClick={handleSendImageMessage}
                                disabled={sending || !selectedImage}
                            >
                                {sending ? '전송 중...' : '이미지 전송'}
                            </button>
                        </>
                    )}
                </div>


                {result && (
                    <div className={`result-message ${result.type}`}>
                        <div className="result-text">{result.message}</div>
                        {result.type === 'success' && (
                            <div className="result-details">
                                <div>메시지 ID: {result.messageId}</div>
                                <div>채팅방 ID: {result.chatRoomId}</div>
                            </div>
                        )}
                    </div>
                )}

            </div>
        </div>
    );
};

export default DirectMessageTest;

